import React from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Box } from '@mui/material';

const CubeFace = ({ transform, color, opacity = 0.1 }) => (
    <Box
        sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: color,
            border: '2px solid rgba(255, 255, 255, 0.2)',
            boxShadow: 'inset 0 0 20px rgba(255, 255, 255, 0.05)',
            transform,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(5px)',
            opacity
        }}
    />
);

const FloatingCube = ({ size = 150, color = 'rgba(34, 211, 238, 0.2)', initialPos = { top: '20%', right: '10%' } }) => {
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const mouseX = useSpring(x, { stiffness: 100, damping: 30 });
    const mouseY = useSpring(y, { stiffness: 100, damping: 30 });

    const rotateX = useTransform(mouseY, [-500, 500], [45, -45]);
    const rotateY = useTransform(mouseX, [-500, 500], [-45, 45]);

    const handleMouseMove = (event) => {
        const { clientX, clientY } = event;
        x.set(clientX - window.innerWidth / 2);
        y.set(clientY - window.innerHeight / 2);
    };

    React.useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    const halfSize = size / 2;

    return (
        <Box
            sx={{
                position: 'absolute',
                ...initialPos,
                perspective: '1200px',
                width: size,
                height: size,
                zIndex: 0,
                pointerEvents: 'none'
            }}
        >
            <motion.div
                style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    transformStyle: 'preserve-3d',
                    rotateX,
                    rotateY,
                }}
                animate={{
                    y: [0, -20, 0],
                }}
                transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            >
                {/* Faces */}
                <CubeFace color={color} transform={`translateZ(${halfSize}px)`} />
                <CubeFace color={color} transform={`rotateY(180deg) translateZ(${halfSize}px)`} />
                <CubeFace color={color} transform={`rotateY(90deg) translateZ(${halfSize}px)`} />
                <CubeFace color={color} transform={`rotateY(-90deg) translateZ(${halfSize}px)`} />
                <CubeFace color={color} transform={`rotateX(90deg) translateZ(${halfSize}px)`} />
                <CubeFace color={color} transform={`rotateX(-90deg) translateZ(${halfSize}px)`} />
                
                {/* Optional glow core */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '25%',
                        left: '25%',
                        width: '50%',
                        height: '50%',
                        background: color,
                        filter: 'blur(30px)',
                        borderRadius: '50%',
                        zIndex: -1
                    }}
                />
            </motion.div>
        </Box>
    );
};

export default FloatingCube;
